import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { X, Settings, Palette, AlertTriangle, AlertCircle, CheckCircle, TrendingUp, Zap, Crosshair, Database, Gauge } from "lucide-react";
import { TargetSpecEditor } from "@/components/TargetSpecEditor";
import { SourceSettings } from "@/components/SourceSettings";
import { ProspectingMetrics } from "@/components/ProspectingMetrics";

const TABS = [
  { id: "spec", label: "Targeting", icon: Crosshair, testId: "settings-tab-spec" },
  { id: "sources", label: "Data Sources", icon: Database, testId: "settings-tab-sources" },
  { id: "metrics", label: "Quality & Usage", icon: Gauge, testId: "settings-tab-metrics" },
  { id: "theme", label: "Theme Preview", icon: Palette, testId: "settings-tab-theme" },
];

// Targeting has ONE editor (the Targeting Spec): onboarding ICP, company size, personas, geos and exclusions all live there.
const SettingsModal = ({ isOpen, onClose, userId }) => {
  const [activeTab, setActiveTab] = useState("spec");

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-gradient-to-br from-[#0f1419] to-[#1a1f24] rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden border border-gray-800 shadow-2xl" data-testid="settings-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#00A38A]/20 to-[#00A38A]/20 border-b border-gray-800 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#00A38A] to-[#00A38A] flex items-center justify-center">
                <Settings className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Targeting Settings</h2>
                <p className="text-gray-400 text-sm">One targeting spec drives Hunt, the Autonomous Stream and the AI judge</p>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors" data-testid="settings-close-btn">
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-800 px-6">
          <div className="flex gap-1 flex-wrap">
            {TABS.map(({ id, label, icon: Icon, testId }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === id ? "border-[#00A38A] text-[#00A38A]" : "border-transparent text-gray-400 hover:text-white"
                }`}
                data-testid={testId}
              >
                <Icon className="h-4 w-4 inline mr-2" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {activeTab === "spec" && <TargetSpecEditor userId={userId} />}
          {activeTab === "sources" && <SourceSettings userId={userId} />}
          {activeTab === "metrics" && <ProspectingMetrics userId={userId} />}

          {activeTab === "theme" && (
            <div className="space-y-6">
              {/* Color Palette */}
              <div>
                <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                  <Palette className="h-5 w-5 text-[#00A38A]" />
                  ISM Brand Color Palette
                </h3>
                <p className="text-gray-400 text-sm mb-6">
                  Your application uses a strict 4-color palette for professional brand consistency.
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* ISM Teal */}
                  <div className="bg-white/5 rounded-xl p-4 border border-gray-700">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-lg bg-[#00A38A] shadow-[0_0_20px_rgba(0,163,138,0.3)]" />
                      <div>
                        <p className="text-white font-medium">ISM Teal</p>
                        <p className="text-gray-500 text-xs font-mono">#00A38A</p>
                      </div>
                    </div>
                    <p className="text-gray-400 text-xs">Primary brand color for buttons, links, active states, and highlights</p>
                  </div>
                  
                  {/* White/Gray */}
                  <div className="bg-white/5 rounded-xl p-4 border border-gray-700">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-white to-gray-400 border border-gray-600" />
                      <div>
                        <p className="text-white font-medium">White / Gray</p>
                        <p className="text-gray-500 text-xs font-mono">#FFFFFF → #6B7280</p>
                      </div>
                    </div>
                    <p className="text-gray-400 text-xs">Text, backgrounds, borders, and neutral UI elements</p>
                  </div>
                  
                  {/* Amber/Orange */}
                  <div className="bg-white/5 rounded-xl p-4 border border-gray-700">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500" />
                      <div>
                        <p className="text-white font-medium">Amber / Orange</p>
                        <p className="text-gray-500 text-xs font-mono">#F59E0B → #F97316</p>
                      </div>
                    </div>
                    <p className="text-gray-400 text-xs">Warnings, alerts, and medium-priority notifications</p>
                  </div>
                  
                  {/* Red */}
                  <div className="bg-white/5 rounded-xl p-4 border border-gray-700">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-red-400 to-red-600" />
                      <div>
                        <p className="text-white font-medium">Red</p>
                        <p className="text-gray-500 text-xs font-mono">#EF4444 → #DC2626</p>
                      </div>
                    </div>
                    <p className="text-gray-400 text-xs">Errors, high-priority alerts, and critical notifications</p>
                  </div>
                </div>
              </div>

              {/* UI Components Preview */}
              <div>
                <h3 className="text-lg font-medium text-white mb-4">UI Components</h3>
                
                {/* Buttons */}
                <div className="bg-white/5 rounded-xl p-4 border border-gray-700 mb-4">
                  <p className="text-gray-400 text-xs mb-3 uppercase tracking-wider">Buttons</p>
                  <div className="flex flex-wrap gap-3">
                    <Button className="bg-[#00A38A] hover:bg-[#008570] text-white">
                      Primary Action
                    </Button>
                    <Button variant="outline" className="border-[#00A38A] text-[#00A38A] hover:bg-[#00A38A]/10">
                      Secondary
                    </Button>
                    <Button variant="outline" className="border-gray-600 text-gray-400 hover:bg-white/10">
                      Neutral
                    </Button>
                    <Button className="bg-amber-500 hover:bg-amber-600 text-white">
                      Warning
                    </Button>
                    <Button className="bg-red-500 hover:bg-red-600 text-white">
                      Danger
                    </Button>
                  </div>
                </div>

                {/* Badges */}
                <div className="bg-white/5 rounded-xl p-4 border border-gray-700 mb-4">
                  <p className="text-gray-400 text-xs mb-3 uppercase tracking-wider">Status Badges</p>
                  <div className="flex flex-wrap gap-3">
                    <Badge className="bg-[#00A38A]/20 text-[#00A38A] border border-[#00A38A]/30">
                      <CheckCircle className="h-3 w-3 mr-1" /> Active
                    </Badge>
                    <Badge className="bg-gray-500/20 text-gray-400 border border-gray-500/30">
                      Pending
                    </Badge>
                    <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30">
                      <AlertTriangle className="h-3 w-3 mr-1" /> At Risk
                    </Badge>
                    <Badge className="bg-red-500/20 text-red-400 border border-red-500/30">
                      <AlertCircle className="h-3 w-3 mr-1" /> Critical
                    </Badge>
                  </div>
                </div>

                {/* Cards */}
                <div className="bg-white/5 rounded-xl p-4 border border-gray-700 mb-4">
                  <p className="text-gray-400 text-xs mb-3 uppercase tracking-wider">Cards & Metrics</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="glass-card-light rounded-lg p-3 border border-[#00A38A]/20">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="h-4 w-4 text-[#00A38A]" />
                        <span className="text-xs text-gray-400">Revenue</span>
                      </div>
                      <p className="text-xl font-bold text-white">$1.2M</p>
                      <p className="text-xs text-[#00A38A]">+12% this month</p>
                    </div>
                    <div className="glass-card-light rounded-lg p-3 border border-amber-500/20">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="h-4 w-4 text-amber-400" />
                        <span className="text-xs text-gray-400">At Risk</span>
                      </div>
                      <p className="text-xl font-bold text-white">3</p>
                      <p className="text-xs text-amber-400">Deals stalled</p>
                    </div>
                    <div className="glass-card-light rounded-lg p-3 border border-red-500/20">
                      <div className="flex items-center gap-2 mb-2">
                        <Zap className="h-4 w-4 text-red-400" />
                        <span className="text-xs text-gray-400">Urgent</span>
                      </div>
                      <p className="text-xl font-bold text-white">1</p>
                      <p className="text-xs text-red-400">Needs action</p>
                    </div>
                  </div>
                </div>

                {/* Progress Indicators */}
                <div className="bg-white/5 rounded-xl p-4 border border-gray-700">
                  <p className="text-gray-400 text-xs mb-3 uppercase tracking-wider">Progress Indicators</p>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-400">Goal Progress</span>
                        <span className="text-[#00A38A]">75%</span>
                      </div>
                      <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                        <div className="h-full w-3/4 bg-[#00A38A] rounded-full" />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-400">Pipeline Health</span>
                        <span className="text-amber-400">45%</span>
                      </div>
                      <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                        <div className="h-full w-[45%] bg-amber-500 rounded-full" />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-400">Risk Level</span>
                        <span className="text-red-400">25%</span>
                      </div>
                      <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                        <div className="h-full w-1/4 bg-red-500 rounded-full" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-800 px-6 py-4 flex items-center justify-end bg-black/20">
          <Button variant="outline" onClick={onClose} className="border-gray-700 text-gray-300 hover:bg-white/10" data-testid="settings-done-btn">
            Done
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
